import { useEffect, useState } from 'react';
import { faNum as fa, formatRial } from '@kia/shared';
import { api } from '../api/client';
import { Card, Kpi } from '../ui';

/**
 * موجودی مواد (میانگین موزون) — perpetual materials inventory valued at the moving weighted-average from
 * PO receipts. Material issues to production (Phase 4b) will reduce it. Read-only (accounting.read).
 */

interface Row {
  materialCode: string;
  onHand: number;
  avgCost: number;
  value: number;
  /** Part of `onHand` with no confirmed price basis: counted in the quantity column, absent from
   *  `avgCost` and `value`. Non-zero is exactly the case where موجودی × بهای میانگین ≠ ارزش. */
  unvaluedQty: number;
}
interface Report {
  rows: Row[];
  totalValue: number;
  unvaluedItems: number;
}

export function InventoryPanel() {
  const [report, setReport] = useState<Report | null>(null);

  useEffect(() => {
    void api<Report>('/api/accounting/inventory').then((r) => {
      if (r.ok) setReport(r.body as Report);
    });
  }, []);

  // One decision, read by the header, every row and the footnote — so the column, the cells and the
  // explanation can never disagree about whether unpriced stock exists.
  const anyUnpriced = (report?.unvaluedItems ?? 0) > 0;

  return (
    <Card title="موجودی مواد (میانگین موزون)">
      {!report || report.rows.length === 0 ? (
        <p style={{ color: 'var(--ink-3)' }}>موجودی‌ای ثبت نشده است.</p>
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: 12 }}>
            <Kpi label="ارزش کل موجودی" value={formatRial(report.totalValue)} />
            <Kpi label="تعداد اقلام" value={fa(report.rows.length)} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>کد ماده</th>
                  <th>موجودی</th>
                  <th>بهای میانگین</th>
                  <th>ارزش</th>
                  {anyUnpriced ? <th>فاقد قیمت قطعی</th> : null}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.materialCode}>
                    <td>{r.materialCode}</td>
                    <td>{fa(r.onHand, 2)}</td>
                    <td>{formatRial(Math.round(r.avgCost))}</td>
                    <td>{formatRial(r.value)}</td>
                    {anyUnpriced ? <td>{r.unvaluedQty > 0 ? fa(r.unvaluedQty, 2) : '—'}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Without this the three columns simply stop multiplying out for a partially-priced material
              (100 priced + 50 unpriced renders 150 / 1,000 / 100,000) with nothing on screen to explain it.
              Shown only when such a row exists, so the wholly-priced book reads exactly as before. */}
          {anyUnpriced ? (
            <p style={{ color: 'var(--ink-3)', marginTop: 8 }}>
              «بهای میانگین» و «ارزش» فقط موجودیِ دارای قیمت قطعی را در بر می‌گیرند، ولی «موجودی»
              همهٔ کالای انبار را می‌شمارد؛ بنابراین برای ماده‌ای که ستون «فاقد قیمت قطعی» دارد،
              حاصل‌ضرب این دو ستون با «ارزش» برابر نمی‌شود. با ثبت قیمت واقعیِ رسید در «خرید و
              انبار» این اختلاف برطرف می‌شود.
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}
